import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Divider, Grid, Text, Title, Box } from "@mantine/core";
import { shallow } from "zustand/shallow";
import { useTranslation } from "react-i18next";

import EffortBuilderForm from "@components/EffortBuilder/EffortBuilderForm";
import {
  populateEffortFields,
  generateEffortFieldRequest,
} from "@components/EffortBuilder/helpers";
import { useAssociateExternalUserData } from "@components/Microsite/useAssociateExternalUserData";
import { MicrositeUserEmailStreams } from "@components/Email";
import { ImageSlider } from "@components/shared";
import ViewLoader from "@components/Microsite/ViewLoader";
import RideChallengeHeader from "./RideChallengeHeader";
import getGeoLocation from "@util/getGeoLocation";
import useAppStore from "@components/Microsite/microsite-store";
import sendActionEvents, { actionIds } from "@components/EntityActionAssociator/sendActionEvents";

export default function EffortView({ preview }) {
  const [effortData, setEffortData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const { associateFromSearchParams } = useAssociateExternalUserData();
  const {
    coords,
    email,
    emailStreams,
    entityInfo,
    location,
    phone,
    setActiveStep,
    setEmailStreams,
    setRegistrationInfo,
    setUser,
    user,
    verificationMethod,
    associateExternalUserDataAttempts,
  } = useAppStore(
    (state) => ({
      coords: state.coords,
      email: state.email,
      emailStreams: state.emailStreams,
      entityInfo: state.entityInfo,
      location: state.location,
      phone: state.phone,
      setActiveStep: state.setActiveStep,
      setEmailStreams: state.setEmailStreams,
      setRegistrationInfo: state.setRegistrationInfo,
      setUser: state.setUser,
      user: state.user,
      verificationMethod: state.verificationMethod,
      associateExternalUserDataAttempts:
        state.associateExternalUserDataAttempts,
    }),
    shallow
  );

  const { t } = useTranslation();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!effortData) return;
    if (getFieldsToDisplay(effortData.configuration.fields).length === 0) {
      onFormSubmit({});
    }
  }, [JSON.stringify(effortData)]);

  function fetchData() {
    setLoading(true);

    const req = {
      campaign_effort_id: entityInfo.campaign_effort_id,
    };

    if (preview) req.preview = true;
    if (location) {
      req.location_id = location.id;
    }

    if (coords) {
      req.latitude = coords.latitude;
      req.longitude = coords.longitude;
    }

    axios
      .post(`/microsite/effort-data/`, req)
      .then(({ data }) => {
        const res = data.response[0];
        const displayFields = getFieldsToDisplay(
          res.configuration.fields || []
        );

        const effortDataToSet = {
          ...res,
          configuration: res.configuration ? res.configuration : {},
        };

        if (displayFields.length === 0) {
          setEffortData(effortDataToSet);
        } else {
          setEffortData(effortDataToSet);
          setLoading(false);
        }
      })
      .catch((err) => {
        setEffortData(null);
      });
  }

  function onFormSubmit(formData) {
    if (!effortData) return;

    const hpField = document.getElementById("requiredReferralId");

    if (hpField && hpField.value) {
      return alert(
        "Success! The form has been submitted. You may now close this page!"
      );
    }

    // if (isAdmin || isManager)
    //   return alert(
    //     `If we did let this form actually submit, the data that would've been submitted would've looked something like:\n\n${JSON.stringify(
    //       formData
    //     )}\n\nFortunately, this didn't submit because you're logged in.`
    //   );

    setSubmitting(true);

    generateEffortFieldRequest(effortData.configuration.fields, formData, user)
      .then((newReq) => {
        submitForm(newReq, formData);
      })
      .catch((err) => {
        console.log(err);
        setSubmitting(false);
      });
  }

  function submitForm(reqData) {
    const req = {
      ...reqData,
      campaign_effort_id: entityInfo.campaign_effort_id,
      submission_method: "form",
    };

    setSubmitting(false);

    if (user) req.user_id = user.id;

    if (!req.demographics.email) {
      if (email) {
        req.demographics.email = email;
      } else {
        if (user && user.email) {
          req.demographics.email = user.email;
        }
      }
    }
    if (phone) req.demographics.mobile_phone = phone;
    if (verificationMethod) req.verification_method = verificationMethod;
    if (coords) {
      req.latitude = coords.latitude;
      req.longitude = coords.longitude;
    }

    if (location) {
      req.location_id = location.id;

      if (location.present_at_location) req.present_at_location = true;

      if (location.geo_spot_id) {
        req.campaign_effort_geo_spot_id = location.geo_spot_id;
        req.geo_spot_id = location.geo_spot_id;
      }
    }

    if (!user) {
      req.email_streams = {
        ...emailStreams.reduce((acc, cur) => {
          acc[cur.name] = cur.value;
          return acc;
        }, {}),
      };
    }

    setSubmitting(true);

    getGeoLocation().then((newCoords) => {
      if (newCoords) {
        req.latitude = newCoords.latitude;
        req.longitude = newCoords.longitude;
      }

      axios
        .post(`/ride-challenge/register/`, req)
        .then(({ data }) => {
          const res = data.response[0];
          setSubmitting(false);
          if (getFieldsToDisplay(effortData.configuration.fields).length > 0) {
            toast.success("Success!");
          }

          sendActionEvents({
            action_id: actionIds.FORM_SUBMIT,
            events: entityInfo?.paid_media_settings?.events || [],
          });

          if (!user) {
            setUser(res.user_lookup);
          }

          if (associateExternalUserDataAttempts > 0) {
            associateFromSearchParams({
              additionalPayload: {
                user_id: user?.id || res.user_lookup.id,
              },
            });
          }

          setRegistrationInfo(res);

          if (
            res.user_lookup &&
            res.user_lookup.hog_membership_data &&
            res.user_lookup.hog_membership_data.confirmed_by_third_party_api
          ) {
            setActiveStep("dashboard");
          } else {
            setActiveStep("membership");
          }
        })
        .catch((err) => {
          toast.error(err);
          setSubmitting(false);
        });
    });
  }

  function getFieldsToDisplay(fields) {
    return populateEffortFields(fields, user, { phone: phone, email: email });
  }

  if (loading)
    return (
      <div>
        <Box mb="sm">
          <RideChallengeHeader />
        </Box>
        <ViewLoader />
      </div>
    );

  if (!effortData || !entityInfo.campaign_effort_id) return null;

  const effortAssets = effortData.assets;
  const assets = effortData.configuration.assets
    ? effortData.configuration.assets
      .map((m) => ({
        ...m,
        asset: effortAssets.find((f) => f.id === m.id),
      }))
      .filter((f) => f.asset)
    : [].filter((f) => f.asset);
  const sliderImages = assets.filter((f) => f.placement === 2);

  const fieldsToDisplay = getFieldsToDisplay(effortData.configuration.fields);

  return (
    <div>
      <Box mb="sm">
        <RideChallengeHeader />
      </Box>
      {sliderImages.length > 0 && (
        <ImageSlider
          items={[
            ...sliderImages.map((m) => ({
              src: m.asset.filename_url,
              external_link_url: m.asset.external_link_url,
              key: m.id,
            })),
          ]}
        />
      )}
      <Title align="center" mt="sm" c="white" order={2}>
        {effortData.title}
      </Title>
      {user && (
        <Text align="center" mb="lg" size="lg" color="white" mt="sm">
          {user.first_name
            ? `${t("user_welcome_title")}, ${user.first_name}!`
            : `${t("user_welcome_title")}!`}
        </Text>
      )}
      <Grid gutter={30}>
        <Grid.Col span={{ base: 12 }}>
          <React.Fragment>
            {fieldsToDisplay && fieldsToDisplay.length > 0 && (
              <EffortBuilderForm
                fields={fieldsToDisplay}
                onSubmit={onFormSubmit}
                loading={submitting}
                buttonText={t("effort_form_submit_button")}
                language="en"
              />
            )}
          </React.Fragment>
        </Grid.Col>
      </Grid>
      <>
        <Divider mt="lg" mb="lg" />
        <MicrositeUserEmailStreams
          user={user}
          streams={emailStreams}
          onChange={(e) => {
            setEmailStreams(e);
          }}
        />
      </>
    </div>
  );
}
